import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import { useState } from 'react';
import { Info, KeyRound, Loader2, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useApiKeys } from '@/hooks/useApiKeys';
import { ApiKey } from '@/types/apiKey';
import { ApiKeyTable } from './ApiKeyTable';
import { ApiKeyCardMobile } from './ApiKeyCardMobile';
import { CreateApiKeyModal } from './CreateApiKeyModal';
import { NewKeyRevealModal } from './NewKeyRevealModal';
import { RevokeApiKeyModal } from './RevokeApiKeyModal';

/**
 * Corpo da tela de chaves de API.
 *
 * TODO o estado de modal mora aqui dentro, e não na página que renderiza o
 * <Layout>. O Layout renderiza os filhos duas vezes — uma árvore para mobile e
 * outra para desktop, alternadas só por CSS —, então estado de abertura que
 * vive acima dele faz as duas instâncias abrirem o modal ao mesmo tempo, em
 * portais empilhados. Aqui, cada instância tem o seu estado e só a que recebeu
 * o clique abre.
 *
 * Isso importa mais neste componente do que em qualquer outro: o modal de
 * revelação carrega o segredo em claro.
 */
export function ApiKeysManager() {
  useLanguage();
  const { apiKeys, isLoading, isError, isCreating, isRevoking, createApiKey, revokeApiKey } =
    useApiKeys();

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [createdKey, setCreatedKey] = useState<{ name: string; key: string } | null>(null);
  const [keyToRevoke, setKeyToRevoke] = useState<ApiKey | null>(null);

  const handleCreate = async (name: string) => {
    try {
      const created = await createApiKey(name);
      setIsCreateOpen(false);
      // O segredo só existe nesta resposta. Guardar no estado é o que permite
      // exibi-lo; ao fechar o modal, ele sai da memória junto.
      setCreatedKey({ name: created.name, key: created.key });
    } catch {
      // O toast de erro já foi disparado pelo hook.
    }
  };

  const handleRevoke = async () => {
    if (!keyToRevoke) return;

    try {
      await revokeApiKey(keyToRevoke.id);
      setKeyToRevoke(null);
    } catch {
      // idem
    }
  };

  const activeCount = apiKeys.filter((key) => key.status === 'active').length;

  return (
    <div>
      <div className="mb-6 flex gap-2.5 rounded-xl border border-[#E2E8F0] bg-[#F8FAFC] p-4">
        <Info className="w-4 h-4 text-[#437DFF] shrink-0 mt-0.5" />
        <div className="text-[13px] leading-relaxed text-[#475569]">
          <p>
            {translateInterfaceText("Uma chave de API permite que uma ferramenta externa leia, em seu nome, as métricas dos seus vídeos e a lista de vídeos da conta — hoje, o connector da HostVSL no Claude. Ela é de leitura: não altera vídeos, não acessa seus dados de cobrança e não faz login no painel.")}</p>
          <p className="mt-2">
            {translateInterfaceText("Se você adicionar o connector numa organização do Claude (planos Team ou Enterprise), a chave passa a ser compartilhada por toda a equipe — todos os membros verão as métricas da conta HostVSL de quem a cadastrou.")}</p>
        </div>
      </div>

      <div className="mb-5 flex items-center justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-[#111]">{translateInterfaceText("Suas chaves")}</h2>
          <p className="text-[13px] text-[#64748B] mt-0.5">
            {activeCount === 0
              ? translateInterfaceText("Nenhuma chave ativa")
              : `${activeCount} ${activeCount === 1 ? 'chave ativa' : 'chaves ativas'}`}
          </p>
        </div>

        <Button
          onClick={() => setIsCreateOpen(true)}
          className="bg-[#437DFF] hover:bg-[#355FCC] h-[42px] rounded-[10px] font-semibold whitespace-nowrap"
        >
          <Plus className="w-4 h-4 mr-1.5" />
          {translateInterfaceText("Nova chave")}</Button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-[#437DFF]" />
        </div>
      ) : isError ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <h3 className="text-lg font-semibold text-[#111] mb-2">
            {translateInterfaceText("Não foi possível carregar suas chaves")}</h3>
          <p className="text-sm text-[#666]">{translateInterfaceText("Recarregue a página para tentar de novo.")}</p>
        </div>
      ) : apiKeys.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <div className="w-12 h-12 rounded-full bg-blue-50 flex items-center justify-center mb-4">
            <KeyRound className="w-5 h-5 text-[#437DFF]" />
          </div>
          <h3 className="text-lg font-semibold text-[#111] mb-2">{translateInterfaceText("Nenhuma chave ainda")}</h3>
          <p className="text-sm text-[#666] mb-6 max-w-[420px]">
            {translateInterfaceText("Crie uma chave para conectar o Claude à sua conta e perguntar sobre as métricas dos seus vídeos por conversa.")}</p>
          <Button
            onClick={() => setIsCreateOpen(true)}
            className="bg-[#437DFF] hover:bg-[#355FCC] rounded-[10px] font-semibold"
          >
            <Plus className="w-4 h-4 mr-1.5" />
            {translateInterfaceText("Criar primeira chave")}</Button>
        </div>
      ) : (
        <>
          <div className="hidden md:block">
            <ApiKeyTable apiKeys={apiKeys} onRevoke={setKeyToRevoke} />
          </div>
          <div className="md:hidden space-y-3">
            {apiKeys.map((apiKey) => (
              <ApiKeyCardMobile key={apiKey.id} apiKey={apiKey} onRevoke={setKeyToRevoke} />
            ))}
          </div>
        </>
      )}

      <CreateApiKeyModal
        open={isCreateOpen}
        onOpenChange={setIsCreateOpen}
        onCreate={handleCreate}
        isCreating={isCreating}
      />

      <NewKeyRevealModal
        apiKey={createdKey?.key ?? null}
        keyName={createdKey?.name ?? ''}
        onClose={() => setCreatedKey(null)}
      />

      <RevokeApiKeyModal
        apiKey={keyToRevoke}
        onOpenChange={(open) => {
          if (!open && !isRevoking) setKeyToRevoke(null);
        }}
        onConfirm={handleRevoke}
      />
    </div>
  );
}
